import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';
import DataTable from '../../../components/ui/DataTable';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import { useAuth } from '../../../context/AuthContext';
import ReferenceCheckFormDrawer from '../ReferenceCheckFormDrawer';
import PersonCell from '../../../components/ui/PersonCell';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) => {
    switch ($status) {
      case 'completed': return '#E8F7EE';
      case 'requested': return '#FFF3CD';
      case 'unreachable': return '#F8D7DA';
      case 'declined': return '#E2E3E5';
      default: return '#F1F5F9';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'completed': return '#1B7A3E';
      case 'requested': return '#856404';
      case 'unreachable': return '#721C24';
      case 'declined': return '#383D41';
      default: return '#64748B';
    }
  }};
`;

export default function ApplicationReferenceChecksTab({ applicationId }) {
  const { t, i18n } = useTranslation();
  // Reference checks are owned by the HR that runs the hiring process (AT.Write).
  // The hiring manager is an AT.Read-only participant — they may view references
  // but must not add/edit/delete them (backend already enforces AT.Write on those
  // endpoints; this hides the actions so they don't 403 on click).
  const { canWrite } = useAuth();
  const canWriteAts = canWrite ? canWrite('AT') : false;
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!applicationId) return;
    setFetching(true);
    api.get(`/v1/ats-reference-checks/for-application/${applicationId}`)
      .then((res) => {
        const result = res.data?.data;
        setData(Array.isArray(result) ? result : (result?.data || []));
      })
      .finally(() => setFetching(false));
  }, [applicationId, refreshTrigger]);

  const handleNew = useCallback(() => { setSelectedRow(null); setDrawerOpen(true); }, []);
  const handleEdit = useCallback((row) => { setSelectedRow(row); setDrawerOpen(true); }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteConfirm.id) return;
    setDeleting(true);
    try {
      await api.delete(`/v1/ats-reference-checks/${deleteConfirm.id}`);
      refresh();
    } finally {
      setDeleting(false);
      setDeleteConfirm({ isOpen: false, id: null, name: '' });
    }
  }, [deleteConfirm.id, refresh]);

  const buildActions = useCallback((row) => {
    const actions = [];
    // Sonuçlanan referans (Tamamlandı/Reddedildi) düzenlemeye kilitlenir —
    // yalnız süreç-içi durumlar (İstendi/Ulaşılamadı) düzenlenebilir.
    if (row.status === 'requested' || row.status === 'unreachable') {
      actions.push({ type: 'edit', label: t('ats.referenceCheck.actions.edit'), onClick: () => handleEdit(row) });
      actions.push({ type: 'divider' });
    }
    actions.push({
      type: 'delete',
      label: t('ats.referenceCheck.actions.delete'),
      onClick: () => setDeleteConfirm({ isOpen: true, id: row.id, name: row.refereeName }),
    });
    return actions;
  }, [t, handleEdit]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'refereeName', label: t('ats.referenceCheck.field.refereeName'), options: { filter: false, sort: true } },
    { name: 'relationship', label: t('ats.referenceCheck.field.relationship'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    { name: 'refereeCompany', label: t('ats.referenceCheck.field.refereeCompany'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    {
      name: 'status',
      label: t('ats.referenceCheck.field.status'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? <StatusBadge $status={v}>{t(`ats.referenceCheck.status.${v}`)}</StatusBadge> : '-'),
      },
    },
    {
      name: 'checkedAt',
      label: t('ats.referenceCheck.field.checkedAt'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? formatDate(v, i18n.language) : '-') },
    },
    { name: 'checkedByUserName', label: t('ats.referenceCheck.field.checkedBy'), options: {
      filter: false, sort: false,
      customBodyRender: (v, tableMeta) => (
        v ? <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.checkedByProfilePicture} /> : '-'
      ) } },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row || !canWriteAts) return null;
          return <OptionsMenu actions={buildActions(row)} row={row} />;
        },
      },
    },
  ], [t, i18n.language, data, buildActions, canWriteAts]);

  return (
    <div>
      <ReferenceCheckFormDrawer
        open={drawerOpen}
        applicationId={applicationId}
        reference={selectedRow}
        onClose={() => setDrawerOpen(false)}
        onSaved={refresh}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('ats.referenceCheck.actions.delete')}
        message={t('ats.referenceCheck.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
        loading={deleting}
      />

      <DataTable
        title={t('ats.referenceCheck.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        serverSide={false}
        pageSize={10}
        canCreateNewRecord={canWriteAts}
        onNewButtonClick={handleNew}
      />
    </div>
  );
}
